import LandscapeChainPrompt from "@/components/shopwindow/LandscapeChainPrompt";
import CSSTransition from "react-transition-group/CSSTransition";
import { eventNames } from "@/modules/Constants";
import RightIcon from "@svg/right.svg";
import LandscapeNav from "@/components/player/LandscapeNav";
import PixieToast from "@/components/player/PixieToast";
import ShopWindow from "@/components/shopwindow/ShopWindow";
import type { ExperienceLayoutContext } from "@/components/player/experienceView";

/**
 * Mobile (phone-scale) landscape and pseudo-fullscreen: the video-first
 * immersive layout. A render function, not a component: Experience returns this
 * root div itself, so the container node and every child slot stay where the
 * class rendered them.
 */
export function renderImmersiveLayout(ctx: ExperienceLayoutContext) {
  const { view, experienceState, onApplicationEvent, video, controlBar, refs } =
    ctx;
  const { sizes, shopWindowConfig, askConfig, askOpen, pixieClass } = view;
  const applicationData = experienceState.applicationData;

  // Closing the sheet (✕, scrim, or a rightward swipe) always goes through
  // the real shop-window-exit event — the same one MethodShopWindow/
  // ListShopWindow/ProductShopWindow's own header exit buttons dispatch —
  // so appController.onShopWindowExit runs (resets SHOP_WINDOW_CONFIG to
  // the default AND, in landscape, closes the sheet). Defined once here so
  // tap and swipe call the identical function, never a parallel path.
  const onCloseShopWindow = () =>
    onApplicationEvent({
      name: eventNames.SHOP_WINDOW_EXIT,
      region: "Shop Window",
      shopWindowConfig,
    });

  return (
    <div
      ref={ctx.setContainerEl}
      className={`experience-container experience-container--landscape${pixieClass}`}
      style={{
        width: sizes.container.width,
        height: sizes.container.height,
        marginTop: sizes.container.margins.top,
        marginLeft: sizes.container.margins.left,
      }}
    >
      <div
        className="video-window-panel video-window-panel--landscape"
        style={{ backgroundColor: "#000" }}
      >
        {video}
        {controlBar}
        <PixieToast />
      </div>
      {/* Round per-tab launcher buttons on the right edge. Tapping one
          selects that tab (SET_SHOP_WINDOW), which in landscape opens the
          sheet (appController.setShopWindow → openShopWindow). No swipe. */}
      <LandscapeNav
        experienceState={experienceState}
        onApplicationEvent={onApplicationEvent}
      />
      {/* Nudge the viewer to rotate for the full landscape view. CSS
          hides it in landscape and auto-fades it after a few seconds. */}
      {typeof window !== "undefined" && window.__pixPseudoFs ? (
        <div className="pix-rotate-hint" aria-hidden="true">
          <svg
            className="pix-rotate-hint__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3a9 9 0 1 0 9 9" />
            <polyline points="21 3 21 8 16 8" />
          </svg>
          Turn your phone for full view
        </div>
      ) : null}
      <CSSTransition
        nodeRef={refs.landscapeSheet}
        classNames="landscape-sheet"
        in={applicationData.showShopWindow() && !askOpen}
        mountOnEnter
        unmountOnExit
        timeout={300}
      >
        <div className="landscape-sheet" ref={refs.landscapeSheet}>
          <div
            className="landscape-scrim"
            aria-hidden="true"
            onClick={onCloseShopWindow}
          />
          <div className="landscape-sheet-panel">
            <button
              type="button"
              className="landscape-sheet-close hover type-3 button square"
              onClick={onCloseShopWindow}
              aria-label="Close recipe menu"
            >
              <RightIcon className="centered icon-right-d" />
            </button>
            <ShopWindow
              isOverlay
              askInWorkspace
              experienceState={experienceState}
              hasHeader={false}
              onApplicationEvent={onApplicationEvent}
            />
          </div>
        </div>
      </CSSTransition>
      {askConfig ? ctx.renderPixieWorkspace(askOpen, null, true) : null}
      {/* Landscape immersive Up Next / recommendations popup — a bottom-left
          card over the video that reveals near the end of playback (or on the
          final Method step) and owns the auto-advance countdown. Products +
          retailers are still reached through the shop-window sheet. */}
      <LandscapeChainPrompt
        experienceState={experienceState}
        onApplicationEvent={onApplicationEvent}
      />
    </div>
  );
}
